import React, { useMemo } from 'react';
import { AnalysisResult } from '../../contracts/scoring';
import { ArticulationVisualizer } from '../../components/ArticulationVisualizer';
import { AdviceCard } from '../../components/SmartTrainer/AdviceCard';

interface Props {
  result: AnalysisResult;
}

type MetricCard = { label: string; value: string; hint?: string };

const safeNum = (n: any) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const clamp0to100 = (n: number) => Math.max(0, Math.min(100, n));

const fmt = (n: any, decimals = 0) => {
  const x = safeNum(n);
  if (x === null) return '—';
  return decimals > 0 ? x.toFixed(decimals) : String(Math.round(x));
};

export const ArticulationTab: React.FC<Props> = ({ result }) => {
  const duration = result.session.duration || 30;

  const raw = safeNum(result.session.metrics.articulation);
  const score = clamp0to100(Math.round(raw ?? 0));

  const diagnostics = result.feedback.diagnostics.filter(d => d.domain === 'ATTAQUE');

  const dbg = (result.session.metrics.debug || {}) as any;

  const measuredCards: MetricCard[] = useMemo(() => {
    const cards: MetricCard[] = [];

    cards.push({
      label: 'Attack score',
      value: `${fmt(score)}%`,
      hint: 'Plus haut = mieux',
    });

    // Certains moteurs exposent transient_ratio / slope / etc. On n’invente rien : on affiche si dispo.
    if (safeNum(dbg.transient_ratio) !== null) {
      cards.push({
        label: 'Transient ratio',
        value: `${fmt((dbg.transient_ratio || 0) * 100, 0)}%`,
        hint: 'Rapport de transients',
      });
    }

    const stuPeaksLen = Array.isArray(result.rhythm?.stuPeaks) ? result.rhythm!.stuPeaks.length : 0;
    const refPeaksLen = Array.isArray(result.rhythm?.refPeaks) ? result.rhythm!.refPeaks.length : 0;

    cards.push({
      label: 'Stu peaks',
      value: stuPeaksLen > 0 ? `${stuPeaksLen} pts` : '—',
      hint: 'Pics détectés (élève)',
    });

    cards.push({
      label: 'Ref peaks',
      value: refPeaksLen > 0 ? `${refPeaksLen} pts` : '—',
      hint: 'Pics détectés (master)',
    });

    const stuEnvLen = Array.isArray(result.rhythm?.stuEnvelope) ? result.rhythm!.stuEnvelope.length : 0;
    cards.push({
      label: 'Stu envelope',
      value: stuEnvLen > 0 ? `${stuEnvLen} pts` : '—',
      hint: 'Enveloppe d’intensité',
    });

    cards.push({
      label: 'Duration',
      value: `${fmt(duration, 0)} s`,
      hint: 'Fenêtre analysée',
    });

    return cards.slice(0, 6);
  }, [dbg, result.rhythm, score, duration]);

  const levelId = result.coaching_level || 4;

  const stuEnvelope = result.rhythm?.stuEnvelope || [];
  const stuPeaks = result.rhythm?.stuPeaks || [];
  const refPeaks = result.rhythm?.refPeaks || [];

  return (
    <div className="animate-fade-in pb-12 flex flex-col gap-10">
      {/* 1) SCORE + POURQUOI + CONSEILS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">Transient Intensity</span>
          <div className="flex items-baseline gap-2">
            <span className={`text-8xl font-black italic tracking-tighter ${score > 75 ? 'text-emerald-400' : 'text-cyan-400'}`}>
              {score}
            </span>
            <span className="text-xl font-bold text-slate-700">/100</span>
          </div>
          <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[9px] font-black text-slate-400 uppercase tracking-widest italic text-center">
            {score > 70 ? 'Impact Franc ✓' : 'Attaques Molles ou Floues'}
          </div>
        </div>

        <div className="lg:col-span-8 bg-black/40 p-8 rounded-[48px] border border-white/10 flex flex-col justify-center gap-6 shadow-inner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-2xl">⚡</div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-widest">Pourquoi ce score ?</h4>
          </div>

          <p className="text-lg text-slate-300 italic leading-relaxed pl-4 border-l-2 border-cyan-500/40">
            "L'attaque mesure la netteté du front montant : plus votre médiator génère un pic d’énergie clair et rapide,
            plus la synchro devient stable et le groove lisible."
          </p>

          <div className="space-y-3">
            {diagnostics.length > 0 ? (
              diagnostics.map((adv, i) => <AdviceCard key={i} advice={adv} isHighContrast={false} />)
            ) : (
              <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl text-center">
                <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">
                  Dynamique d'attaque validée
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ 2) MESURES DÉTECTÉES (standard) */}
      <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
        <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Mesures détectées</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {measuredCards.map((m, i) => (
            <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{m.label}</div>
              <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
              {m.hint ? <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div> : null}
            </div>
          ))}
        </div>
      </div>

      {/* 3) MODULE */}
      <ArticulationVisualizer
        stuEnvelope={stuEnvelope}
        stuPeaks={stuPeaks}
        refPeaks={refPeaks}
        duration={duration}
        score={score}
        levelId={levelId}
      />

      {/* 4) TIP */}
      <div className="bg-white/5 p-8 rounded-[40px] border border-white/10">
        <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] mb-4">Secret de Pro : le "Rising Edge"</h5>
        <p className="text-xs text-slate-400 italic leading-relaxed">
          Si votre score est bas, inclinez le médiator à ~45°. Moins de frottement = un pic plus net.
          Résultat : attaque plus lisible et synchro plus facile à stabiliser.
        </p>
      </div>
    </div>
  );
};
